import React from 'react';

/**
 * Badge - Status and category indicators
 *
 * Variants:
 * - neutral: Default zinc styling
 * - success: Green for optimal/good
 * - warning: Yellow/amber for moderate
 * - error: Red for issues/warnings
 * - primary: Blue branding
 * - purple: For special categories like "Distance"
 * - orange: For special categories like "Players"
 */

const variantStyles = {
  neutral: 'bg-subtle text-zinc-300',
  success: 'bg-success-muted text-success border border-success/30',
  warning: 'bg-warning-muted text-warning border border-warning/30',
  error: 'bg-error-muted text-error border border-error/30',
  primary: 'bg-primary-muted text-primary border border-primary/30',
  purple: 'bg-purple-900/50 text-purple-300',
  orange: 'bg-orange-900/50 text-orange-300',
  emerald: 'bg-emerald-900/50 text-emerald-300',
  blue: 'bg-blue-900/50 text-blue-300',
  amber: 'bg-amber-900/50 text-amber-300',
};

const sizeStyles = {
  xs: 'text-[9px] px-1.5 py-0.5',
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-2 py-1',
};

export const Badge = ({
  children,
  variant = 'neutral',
  size = 'sm',
  className = '',
  ...props
}) => (
  <span
    className={`inline-flex items-center rounded font-medium ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
    {...props}
  >
    {children}
  </span>
);

/**
 * TrafficLight - Playability status indicator dot
 * Used in matrix cells and detail panels
 */
export const TrafficLight = ({
  status = 'green', // 'green' | 'yellow' | 'red'
  size = 'md',
  className = '',
}) => {
  const colors = {
    green: 'bg-success',
    yellow: 'bg-warning',
    red: 'bg-error',
  };

  const sizes = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  return (
    <div className={`rounded-full ${colors[status]} ${sizes[size]} ${className}`} />
  );
};

/**
 * CategoryBadge - Predefined styling for club categories
 */
export const CategoryBadge = ({ category }) => {
  const categoryStyles = {
    Driver: 'blue',
    Fairway: 'purple',
    Hybrid: 'purple',
    Iron: 'emerald',
    Wedge: 'orange',
    Putter: 'neutral',
  };

  return (
    <Badge variant={categoryStyles[category] || 'neutral'} size="sm">
      {category}
    </Badge>
  );
};

/**
 * StatusBadge - For gap analysis status
 */
export const StatusBadge = ({ status }) => {
  const statusStyles = {
    ideal: 'success',
    moderate: 'warning',
    large: 'error',
    tight: 'amber',
  };

  return (
    <Badge variant={statusStyles[status] || 'neutral'} size="sm">
      {status}
    </Badge>
  );
};

/**
 * PlayabilityBadge - Full playability status with label and warning
 */
export const PlayabilityBadge = ({ status, warning }) => {
  const labels = {
    green: 'Good Playability',
    yellow: 'Moderate Playability',
    red: 'Low Playability',
  };

  const styles = {
    green: 'bg-success-muted border-success/50 text-success',
    yellow: 'bg-warning-muted border-warning/50 text-warning',
    red: 'bg-error-muted border-error/50 text-error',
  };

  const tooltips = {
    green: 'This combo has good forgiveness and dispersion control for your swing. Recommended for consistent performance.',
    yellow: 'This combo may have moderate dispersion or forgiveness concerns. Consider your skill level and consistency needs.',
    red: 'This combo may produce inconsistent results or require precise ball-striking. Better options may be available.',
  };

  return (
    <div
      className={`p-3 rounded-lg flex items-center gap-3 border ${styles[status]}`}
      title={tooltips[status]}
    >
      <TrafficLight status={status} size="md" />
      <div className="flex-1">
        <div className="text-sm font-medium">{labels[status]}</div>
        {warning && <div className="text-xs text-zinc-400 mt-0.5">{warning}</div>}
        {!warning && <div className="text-xs text-zinc-400 mt-0.5">{tooltips[status]}</div>}
      </div>
    </div>
  );
};

